import { CheckCircleIcon, ExclamationCircleIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { CustomAttributeFields } from '@/components/account-profile/CustomAttributeFields';
import { ProfileImageField } from '@/components/account-profile/ProfileImageField';
import { Button } from '@/components/core/Button';
import { Checkbox } from '@/components/core/Checkbox';
import { Icon } from '@/components/core/Icon';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { Textarea } from '@/components/core/Textarea';
import { Tooltip } from '@/components/core/Tooltip';
import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { omitUnchangedCustomValues } from './changedCustomValues';
import { cloneOrganization, organizationBridge, organizationImageUrl, OrganizationRecord } from './organizationBridge';

const fieldLabelClassName = 'tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base';

function toSentenceCase(value: string) {
    if (!value) {
        return value;
    }
    const lower = value.toLocaleLowerCase();
    return lower.charAt(0).toLocaleUpperCase() + lower.slice(1);
}

function OrganizationPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { auth } = useAuth();
    const canEdit = Boolean(
        auth?.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_UI, 'edit.organizationdata'),
    );
    const originalRef = useRef<OrganizationRecord>({});
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const methods = useForm<OrganizationRecord>({
        defaultValues: {
            name: '',
            gln: '',
            url: '',
            email: '',
            address: '',
            localeSpecificNumberFormat: false,
            customValues: {},
        },
    });
    const { control, formState: { isDirty }, handleSubmit, register, reset, watch } = methods;
    const organizationId = watch('id');
    const verified = watch('verified');
    const hasImage = Boolean(watch('hasImage'));
    const imageUrl = watch('imageUrl');

    useEffect(() => {
        const { organizationService } = organizationBridge();
        organizationService.getOrganization(false);
        const subscription = organizationService.$organization.subscribe((organization) => {
            if (!organization) {
                return;
            }
            const snapshot = cloneOrganization(organization);
            originalRef.current = snapshot;
            reset(cloneOrganization(organization));
            setReady(true);
        });
        return () => subscription.unsubscribe();
    }, [reset]);

    const save = (values: OrganizationRecord) => {
        const customValues = omitUnchangedCustomValues(
            values.customValues,
            originalRef.current.customValues,
        );
        const payload: OrganizationRecord = {
            ...originalRef.current,
            ...values,
            customValues: customValues ?? values.customValues,
        };
        setSaving(true);
        const { organizationResource, organizationService, rootScope } = organizationBridge();
        organizationResource.save({}, payload, (response) => {
            organizationService.update(response);
            rootScope.$broadcast('organizationUpdated');
            toast.success(t('SAVE_SUCCESS_MESSAGE'));
            setSaving(false);
        }, (response) => {
            rootScope.error = response.data;
            toast.error(t('SAVE_ERROR_MESSAGE'));
            setSaving(false);
        });
    };

    const cancel = () => {
        reset(cloneOrganization(originalRef.current));
    };

    if (!ready) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
                <Loader />
            </div>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2">
                <Icon icon={GlobeAltIcon} size="sm" variant="dark" />
                <h1 className="tw-m-0 tw-text-t-base tw-font-medium tw-text-t-primary lg:tw-text-t-lg">
                    {toSentenceCase(t('ORGANIZATION_DATA'))}
                </h1>
            </div>
            <div className="tw-rounded-lg tw-bg-white tw-p-5">
                <form
                    className="tw-flex tw-flex-col tw-gap-6 md:tw-flex-row md:tw-items-start"
                    onSubmit={handleSubmit(save)}
                >
                    <ProfileImageField
                        variant="organization"
                        canEdit={canEdit}
                        hasImage={hasImage}
                        imageUrl={typeof imageUrl === 'string' ? imageUrl : undefined}
                        onUploaded={() => {
                            const { organizationService, rootScope } = organizationBridge();
                            organizationService.update({ hasImage: true });
                            organizationService.update({ imageUrl: organizationImageUrl() });
                            rootScope.$broadcast('updateOrganizationData');
                            organizationService.getOrganization(true);
                        }}
                    />

                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-4">
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <label htmlFor="organizationName" className={fieldLabelClassName}>
                                {toSentenceCase(t('COMPANY_NAME'))}
                            </label>
                            <Input
                                id="organizationName"
                                placeholder={toSentenceCase(t('COMPANY_NAME'))}
                                readOnly={!canEdit}
                                {...register('name')}
                            />
                        </div>

                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <div className="tw-flex tw-items-center tw-gap-1">
                                <label htmlFor="organizationGln" className={fieldLabelClassName}>
                                    {t('GLN')}
                                </label>
                                <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center">
                                    <Tooltip popupContent={t('VERIFY_ORGANIZATION_INFO_MESSAGE')} />
                                </span>
                            </div>
                            <Input
                                id="organizationGln"
                                placeholder={t('GLN')}
                                readOnly={!canEdit}
                                icon={organizationId != null && organizationId !== ''
                                    ? (verified ? CheckCircleIcon : ExclamationCircleIcon)
                                    : undefined}
                                iconVariant={verified ? 'success' : 'destructive'}
                                {...register('gln')}
                            />
                        </div>

                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <label htmlFor="organizationWebsite" className={fieldLabelClassName}>
                                {t('WEBSITE')}
                            </label>
                            <Input
                                id="organizationWebsite"
                                placeholder={t('WEBSITE')}
                                readOnly={!canEdit}
                                {...register('url')}
                            />
                        </div>

                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <label htmlFor="organizationEmail" className={fieldLabelClassName}>
                                {t('EMAIL')}
                            </label>
                            <Input
                                id="organizationEmail"
                                placeholder={t('EMAIL')}
                                readOnly={!canEdit}
                                {...register('email')}
                            />
                        </div>

                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <label htmlFor="organizationAddress" className={fieldLabelClassName}>
                                {t('ADDRESS')}
                            </label>
                            <Textarea
                                id="organizationAddress"
                                rows={10}
                                placeholder={t('ADDRESS')}
                                readOnly={!canEdit}
                                {...register('address')}
                            />
                        </div>

                        <Controller
                            name="localeSpecificNumberFormat"
                            control={control}
                            render={({ field }) => (
                                <label htmlFor="localeSpecificNumberFormat" className="tw-flex tw-items-center tw-gap-2 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                    <Checkbox
                                        id="localeSpecificNumberFormat"
                                        isChecked={Boolean(field.value)}
                                        disabled={!canEdit}
                                        onCheckedChange={(checked) => field.onChange(checked === true)}
                                    />
                                    <span>{t('LOCALE_SPECIFIC_NUMBER_FORMAT')}</span>
                                </label>
                            )}
                        />

                        <FormProvider {...methods}>
                            <CustomAttributeFields variant="organization" canEdit={canEdit} />
                        </FormProvider>

                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <Button id="organization_save" type="submit" disabled={!canEdit || saving || !isDirty} loading={saving}>
                                {t('ITEM.SAVE_CHANGES')}
                            </Button>
                            <Button
                                id="organization_cancel"
                                type="button"
                                variant="secondary"
                                disabled={!canEdit || saving || !isDirty}
                                onClick={cancel}
                            >
                                {t('CANCEL')}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default OrganizationPage;
